import type { Strike, StrikeResult, GameState } from '../types/game-state';
import type { City } from '../types/world';

// Reference radii at 1 kt (surface burst), in km
const BLAST_RADII_1KT = {
  fireball: 0.13,
  psi20: 0.17,     // reinforced concrete destroyed
  psi5: 0.47,      // most buildings destroyed
  psi3: 0.68,      // residential buildings destroyed
  psi1: 1.5,       // windows shattered, light damage
} as const;

function blastRadiusKm(yieldKt: number, referenceKm: number): number {
  return referenceKm * Math.cbrt(yieldKt);
}

function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function estimateCityCasualties(
  city: City,
  warheadYieldKt: number,
  mirvCount: number,
): { casualties: number; infrastructureDamage: number; destroyed: boolean } {
  // Each MIRV warhead detonates separately — model as N independent blasts
  // covering N times the area of one warhead, not as one combined yield.
  const singleLethalRadius = blastRadiusKm(warheadYieldKt, BLAST_RADII_1KT.psi5);
  const singleDamageRadius = blastRadiusKm(warheadYieldKt, BLAST_RADII_1KT.psi1);

  // Estimate city radius from population (rough urban density model)
  const cityRadiusKm = Math.sqrt(city.currentPopulation / (5000 * Math.PI));

  // N warheads cover N times the lethal area (with overlap diminishing returns)
  const singleLethalArea = singleLethalRadius ** 2;
  const singleDamageArea = singleDamageRadius ** 2;
  const cityArea = cityRadiusKm ** 2;

  const lethalFraction = Math.min(1, (mirvCount * singleLethalArea) / cityArea);
  const damageFraction = Math.min(1, (mirvCount * singleDamageArea) / cityArea);

  const killed = Math.round(city.currentPopulation * lethalFraction * 0.85);
  const injured = Math.round(city.currentPopulation * (damageFraction - lethalFraction) * 0.35);
  const casualties = Math.min(city.currentPopulation, killed + injured);

  const infrastructureDamage = Math.min(1, lethalFraction * 0.8 + (damageFraction - lethalFraction) * 0.2);
  const destroyed = lethalFraction > 0.7;

  return { casualties, infrastructureDamage, destroyed };
}

function probabilityOfKill(yieldKt: number, cepMeters: number, hardnessPsi: number): number {
  // Lethal radius for a hardened target: r = Y^(1/3) * k / H^(1/3)
  // Simplified Pk formula
  const lethalRadiusM = Math.cbrt(yieldKt * 1000) * 30 / Math.cbrt(hardnessPsi);
  const cepKm = cepMeters / 1000;
  const lethalKm = lethalRadiusM / 1000;
  return 1 - 0.5 ** ((lethalKm / cepKm) ** 2);
}

function executeStrike(strike: Strike, state: GameState): StrikeResult[] {
  const results: StrikeResult[] = [];
  const attackerArsenal = strike.attackerSide === 'US' ? state.usArsenal : state.ussrArsenal;
  const targetCities = strike.attackerSide === 'US' ? state.world.ussrCities : state.world.usCities;
  const targetInstallations = strike.attackerSide === 'US' ? state.world.ussrInstallations : state.world.usInstallations;

  for (const assignment of strike.assignments) {
    const weapon = attackerArsenal.weapons.find(w => w.id === assignment.weaponId);
    if (!weapon || weapon.status !== 'ready') continue;

    // Mark weapon as expended
    weapon.status = 'expended';

    if (assignment.targetType === 'city') {
      const city = targetCities.find(c => c.id === assignment.targetId);
      if (!city || city.isDestroyed || city.currentPopulation <= 0) {
        results.push({
          targetId: assignment.targetId,
          targetType: 'city',
          weaponDetonated: true,
          yieldKt: weapon.system.yieldKt,
          casualties: 0,
          infrastructureDamage: 0,
          targetDestroyed: false,
          targetName: city?.name ?? 'UNKNOWN',
        });
        continue;
      }

      const { casualties, infrastructureDamage, destroyed } = estimateCityCasualties(
        city,
        weapon.system.yieldKt,
        weapon.system.mirvCount,
      );

      // Deduct casualties from city population immediately so subsequent
      // hits against the same city in this strike don't double-count.
      city.currentPopulation = Math.max(0, city.currentPopulation - casualties);
      if (destroyed) city.isDestroyed = true;

      results.push({
        targetId: city.id,
        targetType: 'city',
        weaponDetonated: true,
        yieldKt: weapon.system.yieldKt * weapon.system.mirvCount,
        casualties,
        infrastructureDamage,
        targetDestroyed: destroyed,
        targetName: city.name,
      });
    } else {
      const installation = targetInstallations.find(i => i.id === assignment.targetId);
      if (!installation || installation.isDestroyed) {
        results.push({
          targetId: assignment.targetId,
          targetType: 'installation',
          weaponDetonated: true,
          yieldKt: weapon.system.yieldKt,
          casualties: 0,
          infrastructureDamage: 0,
          targetDestroyed: false,
          targetName: installation?.name ?? 'UNKNOWN',
        });
        continue;
      }

      const pk = probabilityOfKill(weapon.system.yieldKt, weapon.system.cepMeters, installation.hardnessPsi);
      const destroyed = Math.random() < pk;

      // Collateral casualties from hitting military targets near cities
      const nearbyCities = targetCities.filter(c => {
        const dist = distanceKm(c.coord.lat, c.coord.lon, installation.coord.lat, installation.coord.lon);
        return dist < 50 && !c.isDestroyed;
      });
      let collateralCasualties = 0;
      for (const city of nearbyCities) {
        const dist = distanceKm(city.coord.lat, city.coord.lon, installation.coord.lat, installation.coord.lon);
        const falloffFactor = Math.max(0, 1 - dist / 50);
        const cityCasualties = Math.min(
          city.currentPopulation,
          Math.round(city.currentPopulation * falloffFactor * 0.02),
        );
        collateralCasualties += cityCasualties;
      }

      results.push({
        targetId: installation.id,
        targetType: 'installation',
        weaponDetonated: true,
        yieldKt: weapon.system.yieldKt,
        casualties: collateralCasualties,
        infrastructureDamage: 0,
        targetDestroyed: destroyed,
        targetName: installation.name,
      });
    }
  }

  return results;
}

export { executeStrike, blastRadiusKm, distanceKm, estimateCityCasualties, probabilityOfKill, BLAST_RADII_1KT };
